import {Color} from '@benchling/nucleus-ui';
import {css, StyleDeclarationValue, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {assert} from 'Asserts';
import {FONT_PROXIMA_11, LEGACY_FONT_PROXIMA_TITLE} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {assertNever} from 'util/Types';

export enum StepItemStatus {
  UNVISITED = 'unvisited',
  DONE = 'done',
  SKIPPED = 'skipped',
  WARNING = 'warning',
  ERROR = 'error',
}

export enum StepNavVariant {
  FLAT = 'flat',
  STACKED = 'stacked',
}

export enum StepNavSize {
  SMALL = 'small',
  LARGE = 'large',
  FULL_WIDTH = 'full-width',
}

/** @package */
export const StepItemStatusColor = {
  [StepItemStatus.DONE]: Color.GREEN_400,
  [StepItemStatus.SKIPPED]: Color.BLUE_500,
  [StepItemStatus.WARNING]: Color.ORANGE_400,
  [StepItemStatus.ERROR]: Color.RED_500,
  [StepItemStatus.UNVISITED]: Color.GRAY_700,
};

export interface StepNavProps<T extends string> {
  steps: Array<{key: T; title: string; metadata?: string}>;
  statusByStep: {[key in T]: StepItemStatus | null};
  currentStepKey: T;
  onUpdateCurrentStep: ((stepKey: T) => void) | undefined;
  disabled?: boolean;
  variant?: StepNavVariant;
  size?: StepNavSize;
}

/*
 * A clickable header component for navigating through numbered steps, one of which is currently active.
 */
export default function StepNav<T extends string>({
  steps,
  statusByStep,
  currentStepKey,
  onUpdateCurrentStep,
  disabled,
  variant = StepNavVariant.STACKED,
  size = StepNavSize.SMALL,
}: StepNavProps<T>): JSX.Element {
  function isCurrentStep(stepKey: T): boolean {
    return stepKey === currentStepKey;
  }

  function handleSwitchStep(stepKey: T): void {
    // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
    return onUpdateCurrentStep != null ? onUpdateCurrentStep(stepKey) : undefined;
  }

  return (
    <div
      className={classNames(css(styles.stepNav))}
      {...getTestProps({component: 'StepNav', element: 'root'})}
    >
      {steps.map((step, index) => (
        <StepItem<T>
          key={step.key}
          stepKey={step.key}
          title={step.title}
          metadata={step.metadata}
          status={statusByStep[step.key]}
          isActive={isCurrentStep(step.key)}
          stepNumber={index + 1}
          isLastStep={index + 1 === steps.length}
          variant={variant}
          size={size}
          onClick={
            (statusByStep[step.key] &&
              [StepItemStatus.UNVISITED, StepItemStatus.SKIPPED].includes(statusByStep[step.key]!)) ||
            disabled
              ? undefined
              : () => {
                  handleSwitchStep(step.key);
                }
          }
        />
      ))}
    </div>
  );
}

export interface StepItemProps<T> {
  stepKey: T;
  title: string;
  isActive: boolean;
  stepNumber: number;
  isLastStep: boolean;
  variant: StepNavVariant;
  size: StepNavSize;
  onClick: (() => void) | undefined;
  status: StepItemStatus | null;
  metadata?: string;
}

// Component for single step inside navigation stepper.
export function StepItem<T extends string>(props: StepItemProps<T>): JSX.Element {
  const canBeVisited = props.status !== StepItemStatus.UNVISITED;

  return (
    <div
      className={css(
        props.variant === StepNavVariant.FLAT && styles.flat,
        props.size === StepNavSize.FULL_WIDTH && !props.isLastStep && styles.fullWidthItem
      )}
      {...getTestProps({component: 'StepItem', element: 'root'})}
    >
      <div className={css(styles.stepItemContainer)}>
        <div
          className={classNames(
            css(
              styles.stepItemCircle,
              canBeVisited && styles.clickable,
              canBeVisited && !props.isActive && styles.stepItemCircleVisited,
              props.isActive && styles.stepItemCircleActive
            ),
            'step-item-circle'
          )}
          onClick={props.onClick}
        >
          {props.stepNumber}
        </div>
        {props.variant === StepNavVariant.STACKED && !props.isLastStep && (
          <div className={css(styles.stepItemLine, getStepItemLineStyle(props.size))} />
        )}
      </div>
      <div className={css(styles.stepItemLabel, canBeVisited && styles.clickable)} onClick={props.onClick}>
        <div
          className={css(
            props.variant === StepNavVariant.STACKED && !props.isLastStep && getItemTextStyle(props.size),
            styles.stepItemTitle
          )}
        >
          {props.status && <StepItemStatusIcon status={props.status} />}
          <div className={classNames(css(styles.stepItemText), 'step-item-text')}>{props.title}</div>
        </div>
        <div
          style={{color: props.status ? StepItemStatusColor[props.status] : Colors.GRAY_DARKER}}
          className={classNames(
            css(styles.stepItemMetadata, getItemTextStyle(props.size)),
            'step-item-metadata'
          )}
        >
          {props.metadata}
        </div>
      </div>
      {props.variant === StepNavVariant.FLAT && !props.isLastStep && (
        <div className={css(styles.stepItemLine, getStepItemLineStyle(props.size))} />
      )}
    </div>
  );
}

function getItemTextStyle(size: StepNavSize): StyleDeclarationValue | null {
  switch (size) {
    case StepNavSize.FULL_WIDTH:
      return null;
    case StepNavSize.LARGE:
      return styles.largeStepItemText;
    case StepNavSize.SMALL:
      return styles.smallStepItemText;
    default:
      assert(false, `Unexpected size: ${assertNever(size)}`);
  }
}

function getStepItemLineStyle(size: StepNavSize): StyleDeclarationValue {
  switch (size) {
    case StepNavSize.FULL_WIDTH:
      return styles.fullWidthLine;
    case StepNavSize.LARGE:
      return styles.largeLine;
    case StepNavSize.SMALL:
      return styles.smallLine;
    default:
      assert(false, `Unexpected size: ${assertNever(size)}`);
  }
}

/**
 * Render appropriate status icon for each step.
 *
 * @package Exported for testing.
 */
export function StepItemStatusIcon(props: {
  /** The status of the step */
  status: StepItemStatus;
}): JSX.Element | null {
  if (props.status === StepItemStatus.UNVISITED) {
    return null;
  }

  // TODO(BNCH-66791): Casting is a bit misleading here, pending cleanup.
  let icon: BenchlingIconName = props.status as BenchlingIconName;
  if (props.status === StepItemStatus.SKIPPED) {
    icon = 'done';
  }

  return (
    <BenchlingIcon
      icon={icon}
      color={StepItemStatusColor[props.status]}
      className={css(styles.stepItemIcon)}
    />
  );
}

const styles = StyleSheet.create({
  stepNav: {
    display: 'flex',
    flexDirection: 'row',
    cursor: 'default',
  },
  flat: {
    display: 'flex',
    alignItems: 'center',
  },
  fullWidthItem: {
    flex: 1,
  },
  stepItemContainer: {
    display: 'flex',
  },
  stepItemLabel: {
    display: 'flex',
    marginTop: 6,
    marginBottom: 6,
    marginRight: 6,
    alignItems: 'flex-start',
    flexDirection: 'column',
  },
  stepItemTitle: {
    display: 'flex',
    alignItems: 'center',
  },
  stepItemText: {
    color: Colors.GRAY_DARKER,
    ...LEGACY_FONT_PROXIMA_TITLE,
  },
  smallStepItemText: {
    maxWidth: 110,
  },
  largeStepItemText: {
    maxWidth: 230,
  },
  stepItemMetadata: {
    ...FONT_PROXIMA_11,
  },
  stepItemIcon: {
    marginRight: 4,
    marginBottom: 0,
  },
  stepItemCircle: {
    height: 24,
    width: 24,
    borderRadius: '50%',
    backgroundColor: Colors.GRAY,
    color: Colors.WHITE,
    marginRight: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...LEGACY_FONT_PROXIMA_TITLE,
  },
  stepItemCircleActive: {
    backgroundColor: Colors.BLUE_LIGHT,
  },
  stepItemCircleVisited: {
    backgroundColor: Colors.GRAY_DARKER,
  },
  stepItemLine: {
    border: '1px solid',
    borderColor: Colors.GRAY,
    flex: 'none',
    alignSelf: 'center',
    marginRight: '6px',
  },
  fullWidthLine: {
    flex: 1,
  },
  smallLine: {
    width: 80,
  },
  largeLine: {
    width: 200,
  },
  clickable: {
    cursor: 'pointer',
  },
});
